import React from 'react';
import {
  Badge,
  Button,
  Caption1,
  Text,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Edit20Regular } from '@fluentui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEnvironment } from '../../contexts/AppContext';
import { useMsalAuth } from '../../hooks/useMsalAuth';
import { ModelApprovalsService, ApisService, initializeTRAPIClient } from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import { ExpandableTable } from '../../components/ExpandableTable';
import type { Column } from '../../components/ExpandableTable';
import DetailsFlyout from '../../components/DetailsFlyout';
import ModelApprovalReviewForm from '../../components/forms/ModelApprovalReviewForm';
import type { ModelApprovalRecord, ModelApprovalReviewPayload, ModelApprovalState } from '../../types/trapi';

const useStyles = makeStyles({
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalM },
  detail: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    gap: tokens.spacingVerticalXS,
    alignItems: 'start',
    '@media (max-width: 600px)': { gridTemplateColumns: '1fr' },
  },
  label: { color: tokens.colorNeutralForeground3 },
  errorText: { color: tokens.colorPaletteRedForeground1 },
  errorBlock: { color: tokens.colorPaletteRedForeground1, display: 'block', marginBottom: tokens.spacingVerticalS },
  expandedContent: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  buttonRow: { display: 'flex', justifyContent: 'flex-end', gap: tokens.spacingHorizontalS, alignItems: 'center' },
  badgeRow: { display: 'flex', gap: tokens.spacingHorizontalXS, flexWrap: 'wrap' },
});

// detected/under_review are pending review — never published to RUNTIME.
// F12 (PR6 review): only the literal 'detected' state (or the record having
// no state field at all, which the trapi-admin skill documents as the
// pre-review-equivalent of 'detected' for a manually pre-registered record)
// renders as "Detected". Any OTHER value that is not one of the four known
// states must render as "Unknown" rather than silently defaulting to
// "Detected" -- a corrupted/unrecognized stored state must never be
// misrepresented as the normal not-yet-reviewed case.
const KNOWN_STATES: (ModelApprovalState | undefined)[] = ['detected', 'under_review', 'approved', 'rejected', undefined];

function stateBadge(state?: ModelApprovalState) {
  switch (state) {
    case 'approved':
      return <Badge appearance="filled" color="success">approved</Badge>;
    case 'rejected':
      return <Badge appearance="filled" color="danger">rejected</Badge>;
    case 'under_review':
      return <Badge appearance="tint" color="warning">under review</Badge>;
    case 'detected':
    case undefined:
      return <Badge appearance="tint" color="informative">detected</Badge>;
    default:
      return <Badge appearance="tint" color="severe">unknown ({state})</Badge>;
  }
}

function stateBadgeValue(state?: ModelApprovalState) {
  if (KNOWN_STATES.includes(state)) return state || 'detected';
  return 'unknown';
}

export default function ModelApprovalsPage() {
  const styles = useStyles();
  const { currentEnvironment } = useEnvironment();
  const { getToken, account } = useMsalAuth();
  const qc = useQueryClient();

  React.useEffect(() => { initializeTRAPIClient({ environment: currentEnvironment }); }, [currentEnvironment]);

  const query = useQuery<ModelApprovalRecord[]>({
    queryKey: ['admin', 'model-approvals', currentEnvironment],
    queryFn: async () => {
      const token = await getToken();
      return ModelApprovalsService.getModelApprovals({ environment: currentEnvironment, accessToken: token });
    },
    enabled: !!account,
    staleTime: 30_000,
  });

  // F3 (PR6 review): canonical apiPath inventory for the scoped-approval
  // checkbox selector -- sourced from the same ApisService the Admin APIs
  // page uses, so a scoped approval can only ever reference a real apiPath.
  const apiPathsQuery = useQuery<string[]>({
    queryKey: ['admin', 'apis-for-model-approvals', currentEnvironment],
    queryFn: async () => {
      const token = await getToken();
      const apis = await ApisService.getApis({ environment: currentEnvironment, accessToken: token });
      const paths = apis
        .map((a: { apiPath?: string }) => a.apiPath)
        .filter((p: string | undefined): p is string => typeof p === 'string' && p.trim().length > 0);
      return Array.from(new Set(paths)).sort();
    },
    enabled: !!account,
    staleTime: 60_000,
  });

  const allItems = query.data ?? [];

  // Review flyout
  const [flyoutOpen, setFlyoutOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<ModelApprovalRecord | null>(null);

  const reviewMut = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: ModelApprovalReviewPayload }) => {
      const token = await getToken();
      return ModelApprovalsService.reviewModelApproval({ id, environment: currentEnvironment, accessToken: token, payload });
    },
    onSuccess: async () => {
      setFlyoutOpen(false);
      setEditing(null);
      await qc.invalidateQueries({ queryKey: ['admin', 'model-approvals', currentEnvironment] });
    },
    onError: (error: unknown) => { console.error('Model approval review failed:', getErrorMessage(error)); },
  });

  const openReview = (record: ModelApprovalRecord) => {
    reviewMut.reset();
    setEditing(record);
    setFlyoutOpen(true);
  };

  const columns: Column<ModelApprovalRecord>[] = React.useMemo(() => [
    {
      key: 'modelName',
      label: 'Model Name',
      render: (r) => <Text weight="semibold">{r.modelName}</Text>,
      getValue: (r) => r.modelName || '',
      sortable: true,
    },
    {
      key: 'modelProvider',
      label: 'Provider',
      render: (r) => r.modelProvider || <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Unknown</Caption1>,
      getValue: (r) => r.modelProvider || '',
      sortable: true,
      filterable: true,
      hideOnMobile: true,
    },
    {
      key: 'state',
      label: 'State',
      render: (r) => stateBadge(r.state),
      getValue: (r) => stateBadgeValue(r.state),
      sortable: true,
      filterable: true,
    },
    {
      key: 'scope',
      label: 'Scope',
      render: (r) => r.scope || '-',
      getValue: (r) => r.scope || '',
      sortable: true,
      filterable: true,
      hideOnMobile: true,
    },
  ], []);

  const renderExpanded = (r: ModelApprovalRecord) => (
    <div className={styles.expandedContent}>
      <div className={styles.buttonRow}>
        <Button
          size="small"
          appearance="subtle"
          icon={<Edit20Regular />}
          onClick={(e) => { e.stopPropagation(); openReview(r); }}
          aria-label="Review model approval"
        >
          Review / Edit
        </Button>
      </div>
      <div className={styles.detail}>
        <Text className={styles.label}>Approved API Paths</Text>
        <div className={styles.badgeRow}>
          {r.scope === 'scoped' && r.approvedApiPaths?.length ? (
            r.approvedApiPaths.map((p) => <Badge key={p} appearance="outline">{p}</Badge>)
          ) : r.scope === 'global' ? (
            <Caption1>All API paths (global)</Caption1>
          ) : (
            <Caption1>-</Caption1>
          )}
        </div>
        <Text className={styles.label}>Notes</Text>
        <Text>{r.notes || <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>-</Caption1>}</Text>
        <Text className={styles.label}>First Detected</Text>
        <Text>{r.firstDetectedAt ? new Date(r.firstDetectedAt).toLocaleString() : '-'}</Text>
        <Text className={styles.label}>Detected On</Text>
        <Text>{r.firstDetectedOnEndpoint || '-'}</Text>
        {r.reviewedBy && (
          <>
            <Text className={styles.label}>Reviewed By</Text>
            <Text>{r.reviewedBy}</Text>
            <Text className={styles.label}>Reviewed At</Text>
            <Text>{r.reviewedAt ? new Date(r.reviewedAt).toLocaleString() : '-'}</Text>
          </>
        )}
        {r.metadata?.lastModifiedAt && (
          <>
            <Text className={styles.label}>Last Modified</Text>
            <Text>
              {new Date(r.metadata.lastModifiedAt).toLocaleString()}
              {r.metadata.lastModifiedBy ? ` by ${r.metadata.lastModifiedBy}` : ''}
            </Text>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div className={styles.header}>
        <Title2>Model Approvals</Title2>
      </div>
      <Caption1 style={{ display: 'block', marginBottom: tokens.spacingVerticalM, color: tokens.colorNeutralForeground3 }}>
        RAI Approved Models — review detected deployments and approve, reject, or correct model
        approval records. Only <code>approved</code> records are published to the runtime; changes
        here take effect immediately for reject/revoke, and on the next automation cycle for new
        approvals.
      </Caption1>

      {query.error ? (
        <Text className={styles.errorText}>{getErrorMessage(query.error)}</Text>
      ) : (
        <ExpandableTable<ModelApprovalRecord>
          columns={columns}
          items={allItems}
          getRowKey={(r) => r.id}
          renderExpanded={renderExpanded}
          loading={query.isLoading}
          emptyMessage="No model approval records found."
          searchPlaceholder="Search model approvals…"
          defaultSortKey="modelName"
        />
      )}

      <DetailsFlyout
        open={flyoutOpen}
        onClose={() => setFlyoutOpen(false)}
        title={editing ? `Review — ${editing.modelName}` : 'Review'}
        size="medium"
        footer={
          <>
            <Button appearance="secondary" onClick={() => setFlyoutOpen(false)} disabled={reviewMut.isPending}>Cancel</Button>
            <Button appearance="primary" type="submit" form="model-approval-review-form" disabled={reviewMut.isPending}>
              {reviewMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        {reviewMut.isError && (
          <Text className={styles.errorBlock}>{getErrorMessage(reviewMut.error)}</Text>
        )}
        {editing && (
          <ModelApprovalReviewForm
            formId="model-approval-review-form"
            hideActions
            record={editing}
            apiPathOptions={apiPathsQuery.data ?? []}
            isLoading={reviewMut.isPending}
            errorMessage={reviewMut.isError ? getErrorMessage(reviewMut.error) : undefined}
            onSubmit={(payload) => reviewMut.mutate({ id: editing.id, payload })}
            onCancel={() => setFlyoutOpen(false)}
          />
        )}
      </DetailsFlyout>
    </div>
  );
}
